import React, { useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { FileText } from 'lucide-react';
import terms from '../data/termsOfService.json';

export default function TermsOfServicePage() {
  const { theme } = useTheme();
  const { setBreadcrumbItems } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumbItems([{ label: 'Terms of Service', path: '/terms' }]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  const SectionHeading = ({ id, title }) => (
    <h2 id={id} className={`scroll-mt-28 text-xl font-bold ${theme.text} mt-10 mb-3 pb-2 border-b ${theme.border}`}>
      {title}
    </h2>
  );

  const P = ({ children }) => (
    <p className={`${theme.textSecondary} leading-relaxed mb-4`}>{children}</p>
  );

  const UL = ({ items }) => (
    <ul className={`list-disc list-inside ${theme.textSecondary} space-y-2 mb-4 ml-2`}>
      {items.map((item, i) => <li key={i} className="leading-relaxed">{item}</li>)}
    </ul>
  );

  const renderBlock = (block, i) => {
    if (block.type === 'p') return <P key={i}>{block.text}</P>;
    if (block.type === 'ul') return <UL key={i} items={block.items} />;
    if (block.type === 'contact') return (
      <div key={i} className={`${theme.backgroundSecondary} border ${theme.border} rounded-lg p-4 mt-2`}>
        <p className={`font-semibold ${theme.text}`}>{block.company}</p>
        <p className={`${theme.textSecondary} text-sm`}>Email: <a href={`mailto:${block.email}`} className={theme.link}>{block.email}</a></p>
        <p className={`${theme.textSecondary} text-sm`}>Website: <a href={`https://${block.website}`} className={theme.link}>{block.website}</a></p>
      </div>
    );
    return null;
  };

  return (
    <div className={`min-h-screen ${theme.background}`}>
      {/* Header */}
      <div className={`${theme.background} py-4`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className={`${theme.card} border ${theme.border} rounded-xl px-6 py-4 flex items-center gap-3`}>
            <FileText size={22} className={theme.link} />
            <div>
              <p className={`text-lg font-bold leading-tight ${theme.text}`}>Terms of Service</p>
              <p className={`text-xs leading-tight ${theme.textSecondary}`}>Last updated: {terms.lastUpdated}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-8">
        {/* Sidebar TOC — desktop */}
        <aside className="hidden lg:block w-52 flex-shrink-0">
          <div className={`sticky ${theme.card} border ${theme.border} rounded-xl p-4`} style={{ top: '6rem' }}>
            <p className={`text-xs font-semibold uppercase tracking-wider ${theme.textSecondary} mb-3`}>Contents</p>
            <nav className="space-y-0.5">
              {terms.sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className={`block px-2 py-1.5 rounded-lg text-xs transition-colors no-underline ${theme.textSecondary} ${theme.cardHover}`}
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className={`flex-1 min-w-0 ${theme.card} border ${theme.border} rounded-xl p-6 md:p-8`}>
          <P>{terms.intro}</P>

          {terms.sections.map((section) => (
            <div key={section.id}>
              <SectionHeading id={section.id} title={section.title} />
              {section.blocks.map((block, i) => renderBlock(block, i))}
            </div>
          ))}

          <div className={`mt-10 pt-6 border-t ${theme.border}`}>
            <p className={`text-xs ${theme.textSecondary} leading-relaxed`}>{terms.footer}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
